import React, { useState, useRef, useEffect, useCallback } from "react";
import ArrowDropUpIcon from "../../../shared/components/icons/ArrowDropUpIcon";

import { Box } from "../../../shared/components/ui/chakra";
import { Heading, Flex } from "@chakra-ui/react";

type AccordionProps = {
  title: string;
  children: React.ReactNode;
  initialOpen?: boolean;
};

const Accordion = ({
  title,
  children,
  initialOpen = false,
}: AccordionProps) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const contentRef = useRef<HTMLDivElement>(null);
  const innerContentRef = useRef<HTMLDivElement>(null);
  const updateContentHeight = useCallback(() => {
    if (!isOpen) {
      contentRef.current!.style.height = `0px`;
      return;
    }

    if (innerContentRef.current && contentRef.current) {
      contentRef.current.style.height = `${innerContentRef.current.getBoundingClientRect().height}px`;
    }
  }, [isOpen]);

  useEffect(() => {
    updateContentHeight();
  }, [isOpen, updateContentHeight]);

  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        updateContentHeight();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, updateContentHeight]);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Box bgColor="white" borderRadius="8px" overflow="hidden" as="section">
      <Flex
        py={{ base: 3, md: 4 }}
        pl={{ base: 5, md: 9 }}
        pr={{ base: 3, md: 7 }}
        alignItems="center"
        onClick={toggleAccordion}
        cursor="pointer"
        _hover={{ bgColor: "brand.500", color: "white" }}
        transition={"0.2s"}
      >
        <Heading
          as="h3"
          size={{ base: "sm", md: "md" }}
          flex="1"
          textAlign="left"
        >
          {title}
        </Heading>
        <Box
          transform={isOpen ? "rotate(0deg)" : "rotate(180deg)"}
          transition="transform 0.3s ease-in-out"
        >
          <ArrowDropUpIcon boxSize={{ base: 7, md: 10 }} />
        </Box>
      </Flex>
      <Box
        ref={contentRef}
        overflow="hidden"
        opacity={isOpen ? 1 : 0}
        transition="height 0.3s ease-in-out, opacity 0.3s ease-in-out"
      >
        <Box
          ref={innerContentRef}
          px={{ base: 5, md: 9 }}
          pt={{ base: 2, md: 4 }}
          pb={{ base: 5, md: 8 }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Accordion;
